import type { TOptions } from 'i18next';
import i18 from 'i18next';
import type { ReactElement } from 'react';
import React from 'react';

import { getFeatureConfig } from '@luna/feature-config';

import type { TFunction } from './types';
import type { Language, LanguageVariant, LanguageVariantWithCiMode } from './Language';
import { getReactDomServer } from './reactDomServerSingleton';

export function transformReactElementsToHtml(value: ReactElement | string): string {
    if (Array.isArray(value)) {
        return value.map(transformReactElementsToHtml).join('');
    }

    if (!React.isValidElement(value)) {
        return value as string;
    }

    const server = getReactDomServer();
    if (!server) {
        return value?.toString();
    }
    return server.renderToString(value).replace(/<!-- -->/gi, '');
}

export function transformReactElementOptionsToHtml(
    options?: Record<string, ReactElement | unknown> | undefined,
): TOptions {
    if (!options) {
        return {};
    }

    return Object.keys(options).reduce(
        (acc, optionKey) => {
            const optionValue = options[optionKey];

            // TS doesn't allow us to index array using a string, but it would work in the runtime.
            // To bypass that errors, we cast the accumulator to object.
            const accAsObject = acc as Record<string, any>;

            if (typeof optionValue === 'object' && !React.isValidElement(optionValue)) {
                accAsObject[optionKey] = transformReactElementOptionsToHtml(
                    optionValue as Record<string, ReactElement | unknown>,
                );
            } else {
                accAsObject[optionKey] = transformReactElementsToHtml(optionValue as ReactElement);
            }

            return acc;
        },
        (Array.isArray(options) ? [] : {}) as TOptions,
    );
}

export function translationExists(translationKey: string): boolean {
    return isLanguageKeysModeActive() || i18.exists(translationKey);
}

const LANGUAGE_KEYS_MODE_URL_PARAM = 'languageKeysMode';
function isLanguageKeysModeActive(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(LANGUAGE_KEYS_MODE_URL_PARAM) === 'true';
}

type BaseConfig = {
    languageOverrides?: {
        [language: string]: LanguageVariant;
    };
    languageScriptOverrides?: {
        [language: string]: LanguageVariant;
    };
};
type Params = {
    allowCimode?: boolean;
    omitScript?: boolean;
};

export function getCurrentLanguage(
    { allowCimode, omitScript }: Params = { allowCimode: false, omitScript: false },
): Language {
    const { languageOverrides, languageScriptOverrides } = getFeatureConfig<
        {
            base: BaseConfig;
        },
        BaseConfig
    >((config) => config.base ?? {});
    const currentLanguage = i18.language === 'cimode' && !allowCimode ? undefined : i18.language;

    const override = Object.entries(languageOverrides ?? {}).find(
        ([__, languageOverride]) => languageOverride === currentLanguage,
    );
    if (override?.[0]) {
        return override[0] as Language;
    }

    const omittedScriptLanguage = Object.entries(languageScriptOverrides ?? {}).find(
        ([__, languageScriptOverride]) => languageScriptOverride === currentLanguage,
    );
    if (omitScript && omittedScriptLanguage?.[0]) {
        return omittedScriptLanguage[0] as Language;
    }

    const defaultLanguage = getFeatureConfig<{ base: { defaultLanguage: Language } }, Language>(
        (f) => f.base?.defaultLanguage,
    );

    /**
     * If override is not defined & current language is not setup yet,
     * we should try to use the default Language from config.
     */
    return (currentLanguage ?? defaultLanguage) as Language;
}

export const changeI18Language = (language?: LanguageVariantWithCiMode): Promise<TFunction> => {
    return i18.changeLanguage(language);
};
